// src/app/(dashboard)/meter/components/MeterListClient.tsx
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import type { MeterListResponse, MeterRow } from "../types";
import { MeterCard } from "./MeterCard";
import { MeterSearchForm } from "./MeterSearchForm";
import { MeterPagination } from "./MeterPagination";
import { RefreshStatusBadge } from "./RefreshStatusBadge";
import { useIntervalRefresh } from "@/hooks/useIntervalRefresh";
import { computeIdealPageSize, getGridColumnCount } from "../utils/pageFit";

interface MeterListClientProps {
  initialData: MeterListResponse;
  initialError?: string | null;
  q: string;
  status: string;
  meterType: string;
}

const FETCH_ERROR_MESSAGE =
  "ไม่สามารถโหลดข้อมูลมิเตอร์ได้ในขณะนี้ ระบบจะลองเชื่อมต่อใหม่โดยอัตโนมัติ";

export function MeterListClient({
  initialData,
  initialError = null,
  q,
  status,
  meterType,
}: MeterListClientProps) {
  const [data, setData] = useState<MeterListResponse>(initialData);
  const [error, setError] = useState<string | null>(initialError);
  const [isStale, setIsStale] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const pathname = usePathname();
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const [now, setNow] = useState<number>(0);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  const qRef = useRef(q);
  qRef.current = q;
  const statusRef = useRef(status);
  statusRef.current = status;
  const meterTypeRef = useRef(meterType);
  meterTypeRef.current = meterType;

  // Shared lean fetch used both for periodic/manual refresh and for
  // auto-fit's page-size changes — a plain /api/meter call, never a Next.js
  // navigation (which would re-render the whole page tree just to change a
  // count).
  const fetchMeters = useCallback(
    async (targetPage: number, targetPageSize: number) => {
      const qs = new URLSearchParams();
      qs.set("page", String(targetPage));
      qs.set("pageSize", String(targetPageSize));
      if (qRef.current) qs.set("q", qRef.current);
      if (statusRef.current) qs.set("status", statusRef.current);
      if (meterTypeRef.current) qs.set("meterType", meterTypeRef.current);
      qs.set("_t", String(Date.now()));

      const res = await fetch(`/api/meter?${qs.toString()}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`meter fetch failed: ${res.status}`);
      return (await res.json()) as MeterListResponse;
    },
    [],
  );

  const handleRefresh = useCallback(async () => {
    try {
      const fresh = await fetchMeters(data.page, data.pageSize);
      setData(fresh);
      setNow(Date.now());
      setIsStale(false); // Clear stale flag when fresh data arrives
      setError(null);
    } catch {
      // Upstream/network failure — keep showing whatever data is already on
      // screen instead of crashing; the next scheduled refresh retries.
      setError(FETCH_ERROR_MESSAGE);
    }
  }, [data.page, data.pageSize, fetchMeters]);

  const {
    secondsUntilRefresh,
    isRefreshing,
    isOnline,
    triggerManualRefresh,
  } = useIntervalRefresh({
    offsetMinutes: 3.5,
    onRefresh: handleRefresh,
  });

  const refreshBadge = (
    <RefreshStatusBadge
      secondsUntilRefresh={secondsUntilRefresh}
      isRefreshing={isRefreshing}
      isOnline={isOnline}
      onManualRefresh={triggerManualRefresh}
    />
  );

  const searchForm = (
    <MeterSearchForm
      q={q}
      pageSize={data.pageSize}
      status={status}
      meterType={meterType}
    />
  );

  const pagination = (
    <MeterPagination
      page={data.page}
      totalPages={totalPages}
      q={q}
      pageSize={data.pageSize}
      status={status}
      meterType={meterType}
    />
  );

  /* ---- auto-fit page size to the viewport, so a full page never needs scrolling ---- */

  // Applies a new page size via the same lean fetch as handleRefresh (never
  // a Next.js navigation), then updates the address bar in place so the URL
  // stays shareable/reload-correct without triggering a server re-render.
  const applyPageSize = useCallback(
    async (newPageSize: number) => {
      try {
        const fresh = await fetchMeters(1, newPageSize);
        setData(fresh);
        setNow(Date.now());
        setIsStale(false);
        setError(null);

        const params = new URLSearchParams();
        params.set("page", "1");
        params.set("pageSize", String(newPageSize));
        if (qRef.current) params.set("q", qRef.current);
        if (statusRef.current) params.set("status", statusRef.current);
        if (meterTypeRef.current) params.set("meterType", meterTypeRef.current);
        window.history.replaceState(null, "", `${pathname}?${params.toString()}`);
      } catch {
        setError(FETCH_ERROR_MESSAGE);
      }
    },
    [fetchMeters, pathname],
  );

  const recalcPageSize = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || data.items.length === 0) return;

    const cols = getGridColumnCount(window.innerWidth);
    const rows = Math.max(1, Math.ceil(data.items.length / cols));
    const rowHeight = grid.scrollHeight / rows;

    const idealPageSize = computeIdealPageSize({
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      gridTop: grid.getBoundingClientRect().top,
      rowHeight,
    });

    if (idealPageSize !== null && idealPageSize !== data.pageSize) {
      applyPageSize(idealPageSize);
    }
  }, [data.items.length, data.pageSize, applyPageSize]);

  // Re-measure whenever the grid's actual rendered size changes — mount,
  // window resize, or a late reflow (e.g. web fonts swapping in after the
  // first paint). A one-shot measurement on mount can be slightly stale;
  // ResizeObserver keeps re-checking until the real, settled size is seen.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(recalcPageSize, 150);
    });
    observer.observe(grid);

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [recalcPageSize]);

  const lastVisibilityRefreshRef = useRef<number>(0);

  // Refresh on visibility change (tab switch, window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      setNow(Date.now());

      const timeSinceLastRefresh =
        Date.now() - lastVisibilityRefreshRef.current;
      if (timeSinceLastRefresh < 5000) return;

      lastVisibilityRefreshRef.current = Date.now();
      triggerManualRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [triggerManualRefresh]);

  // Refresh on mount (handles back button, forward button, direct navigation)
  useEffect(() => {
    setIsStale(true); // Mark as stale immediately
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="sticky top-14 z-20 bg-slate-50 pt-4 dark:bg-slate-950">
        <div
          className={[
            "flex items-center justify-between gap-3",
            "rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm",
            "dark:border-slate-800 dark:bg-slate-900",
          ].join(" ")}
        >
          <div>
            <h1 className="m-0 text-[22px] font-bold text-slate-900 dark:text-slate-50">
              PEA Meters
            </h1>
            <div className="mt-1 flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
              <span>
                จำนวนมิเตอร์ทั้งหมด:{" "}
                <b className="text-slate-900 dark:text-slate-50">
                  {data.total}
                </b>
              </span>
              <span className="hidden sm:inline-flex">{refreshBadge}</span>
            </div>
          </div>

          <div className="hidden sm:block">{searchForm}</div>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "แสดงน้อยลง" : "แสดงตัวกรองเพิ่มเติม"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 sm:hidden"
          >
            <ChevronDownIcon
              className={expanded ? "rotate-180 transition-transform" : "transition-transform"}
            />
          </button>
        </div>

        {expanded && (
          <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:hidden">
            {refreshBadge}
            {pagination}
            {searchForm}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          {error}
        </div>
      )}

      <div className="mt-4 hidden justify-center sm:flex">{pagination}</div>

      <div
        ref={gridRef}
        className={[
          "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
          "transition-opacity duration-200",
          isRefreshing || isStale ? "opacity-50" : "opacity-100",
        ].join(" ")}
      >
        {now > 0 &&
          data.items.map((meter: MeterRow) => (
            <MeterCard key={meter.id} meter={meter} now={now} />
          ))}

        {data.items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
            ไม่พบข้อมูล
          </div>
        )}
      </div>
    </>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
